import React, {Fragment, useState} from 'react'
import {useLocation, Link} from 'react-router-dom'
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    XMarkIcon,
    ChevronDownIcon,
    ChevronUpIcon,
} from '@heroicons/react/24/outline'
import {Dialog, Transition} from '@headlessui/react'
import PermissionEnum from '../controllers/PermissionEnum'

interface NavItem {
    name: string
    href: string
    icon: React.ComponentType<React.ComponentPropsWithoutRef<'svg'>>
    permission: PermissionEnum
}

interface NavSection {
    title: string
    items: NavItem[]
}

interface SidebarProps {
    sections: NavSection[]
    isCollapsed: boolean
    onToggleCollapse: () => void
    mobileOpen: boolean
    onMobileClose: () => void
    logo: React.ReactNode
    userMenu: React.ReactNode
}

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(' ')
}

const Sidebar: React.FC<SidebarProps> = ({
    sections,
    isCollapsed,
    onToggleCollapse,
    mobileOpen,
    onMobileClose,
    logo,
    userMenu
}) => {
    const location = useLocation()
    // Track which sections are expanded (default all collapsed)
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())

    const isActive = (href: string) => {
        return location.pathname === href || location.pathname.startsWith(href + '/')
    }

    const toggleSection = (sectionTitle: string) => {
        setExpandedSections(prev => {
            const newSet = new Set(prev)
            if (newSet.has(sectionTitle)) {
                newSet.delete(sectionTitle)
            } else {
                newSet.add(sectionTitle)
            }
            return newSet
        })
    }

    const SidebarContent = () => (
        <div className="flex h-full flex-col">
            {/* Logo */}
            <div className="flex h-16 items-center justify-end px-4 border-b border-gray-700">
                {!isCollapsed && (
                    <div className="flex-1">
                        {logo}
                    </div>
                )}
                <button
                    onClick={onToggleCollapse}
                    className="hidden lg:block p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700"
                >
                    {isCollapsed ? (
                        <ChevronRightIcon className="h-5 w-5" />
                    ) : (
                        <ChevronLeftIcon className="h-5 w-5" />
                    )}
                </button>
            </div>

            {/* Navigation */}
            <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-4">
                {sections.map((section) => {
                    const isExpanded = expandedSections.has(section.title)
                    return (
                        <div key={section.title} className="mb-2">
                            {!isCollapsed ? (
                                <button
                                    onClick={() => toggleSection(section.title)}
                                    className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-200 hover:bg-gray-700 rounded-md transition-colors"
                                >
                                    <span>{section.title}</span>
                                    {isExpanded ? (
                                        <ChevronUpIcon className="h-4 w-4" />
                                    ) : (
                                        <ChevronDownIcon className="h-4 w-4" />
                                    )}
                                </button>
                            ) : (
                                <div className="mb-2">
                                    <div className="h-0.5 bg-gray-700 mx-3"></div>
                                    <div className="text-[0.6rem] text-gray-500 text-center uppercase tracking-wider mt-1 px-1 truncate" title={section.title}>
                                        {section.title}
                                    </div>
                                </div>
                            )}
                            {(isExpanded || isCollapsed) && (
                                <div className="space-y-1 mt-1">
                                    {section.items.map((item) => {
                                        const active = isActive(item.href)
                                        return (
                                            <Link
                                                key={item.name}
                                                to={item.href}
                                                className={classNames(
                                                    active
                                                        ? 'bg-gray-900 text-white'
                                                        : 'text-gray-300 hover:bg-gray-700 hover:text-white',
                                                    'group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                                                    isCollapsed ? 'flex-col justify-center' : ''
                                                )}
                                            >
                                                <item.icon
                                                    className={classNames(
                                                        active ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-300',
                                                        'flex-shrink-0 h-6 w-6',
                                                        isCollapsed ? '' : 'mr-3'
                                                    )}
                                                    aria-hidden="true"
                                                />
                                                {isCollapsed ? (
                                                    <span className="text-[0.65rem] mt-1 text-center leading-tight px-1">{item.name}</span>
                                                ) : (
                                                    <span>{item.name}</span>
                                                )}
                                            </Link>
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    )
                })}
            </nav>

            {/* User menu */}
            <div className="flex-shrink-0 border-t border-gray-700 p-4">
                {userMenu}
            </div>
        </div>
    )

    return (
        <>
            {/* Mobile sidebar */}
            <Transition.Root show={mobileOpen} as={Fragment}>
                <Dialog as="div" className="relative z-40 lg:hidden" onClose={onMobileClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-40 flex">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-gray-800">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-in-out duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="ease-in-out duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div className="absolute top-0 right-0 -mr-12 pt-2">
                                        <button
                                            type="button"
                                            className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                                            onClick={onMobileClose}
                                        >
                                            <span className="sr-only">Close sidebar</span>
                                            <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                                        </button>
                                    </div>
                                </Transition.Child>
                                <SidebarContent />
                            </Dialog.Panel>
                        </Transition.Child>
                        <div className="w-14 flex-shrink-0" aria-hidden="true">
                            {/* Dummy element to force sidebar to shrink to fit close icon */}
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* Desktop sidebar */}
            <div className={classNames(
                'hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col bg-gray-800 transition-all duration-300 z-30',
                isCollapsed ? 'lg:w-28' : 'lg:w-56'
            )}>
                <SidebarContent />
            </div>
        </>
    )
}

export default Sidebar
